import { useState } from 'react';
import type { FormPage, FormFieldDef, FormFieldType } from '@hcc/types';
import { Button } from '../../ui/Button';
import { Pill } from '../../ui/Pill';
import { FieldPalette } from './FieldPalette';
import { ConditionEditor, type ConditionSourceField } from './ConditionEditor';
import { FIELD_TYPE_META } from './fieldMeta';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, ChevronUp, ChevronDown } from 'lucide-react';

interface PageEditorProps {
  page: FormPage;
  pageIdx: number;
  totalPages: number;
  /** Non-static fields from earlier pages, to drive this page's visibility. */
  earlierFields: ConditionSourceField[];
  onUpdatePage: (patch: Partial<Pick<FormPage, 'title' | 'description' | 'visible_if'>>) => void;
  onRemovePage: () => void;
  onMovePage: (dir: -1 | 1) => void;
  onAddField: (type: FormFieldType) => void;
  onEditField: (fieldIdx: number) => void;
  onRemoveField: (fieldIdx: number) => void;
  onMoveField: (fieldIdx: number, dir: -1 | 1) => void;
}

const inputClass =
  'block w-full rounded-token-sm border border-line-strong/20 bg-surface px-2.5 py-1.5 text-sm';

function FieldRow({
  field,
  isFirst,
  isLast,
  onEdit,
  onRemove,
  onMove,
}: {
  field: FormFieldDef;
  isFirst: boolean;
  isLast: boolean;
  onEdit: () => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const meta = FIELD_TYPE_META[field.type];
  const Icon = meta.icon;
  return (
    <div className="flex items-center gap-2 rounded-token-sm border border-line/10 bg-surface px-3 py-2">
      <Icon className="h-4 w-4 shrink-0 text-fg-dim" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-fg">{field.label || '(no label)'}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-1">
          <Pill tone="neutral" size="sm">{meta.label}</Pill>
          {field.required ? <Pill tone="info" size="sm">Required</Pill> : null}
          {field.special_category ? <Pill tone="warning" size="sm">Special category</Pill> : null}
          {field.visible_if ? <Pill tone="accent" size="sm">Conditional</Pill> : null}
        </div>
      </div>
      <div className="flex items-center gap-0.5">
        <button type="button" disabled={isFirst} onClick={() => onMove(-1)}
          className="rounded-token-sm p-1.5 text-fg-dim hover:bg-surface-2 disabled:opacity-30" aria-label="Move up">
          <ArrowUp className="h-4 w-4" />
        </button>
        <button type="button" disabled={isLast} onClick={() => onMove(1)}
          className="rounded-token-sm p-1.5 text-fg-dim hover:bg-surface-2 disabled:opacity-30" aria-label="Move down">
          <ArrowDown className="h-4 w-4" />
        </button>
        <button type="button" onClick={onEdit}
          className="rounded-token-sm p-1.5 text-fg-dim hover:bg-surface-2 hover:text-accent" aria-label="Edit field">
          <Pencil className="h-4 w-4" />
        </button>
        <button type="button" onClick={onRemove}
          className="rounded-token-sm p-1.5 text-fg-dim hover:bg-surface-2 hover:text-danger" aria-label="Remove field">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/** Edits one page: title/description, its field list, and an add-field action. */
export function PageEditor(props: PageEditorProps) {
  const { page, pageIdx, totalPages } = props;
  const [paletteOpen, setPaletteOpen] = useState(false);

  return (
    <div className="rounded-token border border-line/10 bg-surface-2 p-3">
      <div className="mb-3 flex items-start gap-2">
        <div className="flex-1 space-y-2">
          <input
            value={page.title ?? ''}
            placeholder={`Page ${pageIdx + 1} title`}
            onChange={(e) => props.onUpdatePage({ title: e.target.value })}
            className={inputClass + ' font-medium'}
          />
          <input
            value={page.description ?? ''}
            placeholder="Page description (optional)"
            onChange={(e) => props.onUpdatePage({ description: e.target.value || undefined })}
            className={inputClass}
          />
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <button type="button" disabled={pageIdx === 0} onClick={() => props.onMovePage(-1)}
            className="rounded-token-sm p-1.5 text-fg-dim hover:bg-surface disabled:opacity-30" aria-label="Move page up">
            <ChevronUp className="h-4 w-4" />
          </button>
          <button type="button" disabled={pageIdx === totalPages - 1} onClick={() => props.onMovePage(1)}
            className="rounded-token-sm p-1.5 text-fg-dim hover:bg-surface disabled:opacity-30" aria-label="Move page down">
            <ChevronDown className="h-4 w-4" />
          </button>
          <button type="button" disabled={totalPages <= 1} onClick={props.onRemovePage}
            className="rounded-token-sm p-1.5 text-fg-dim hover:bg-surface hover:text-danger disabled:opacity-30" aria-label="Remove page">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mb-3">
        <ConditionEditor
          condition={page.visible_if}
          sources={props.earlierFields}
          onChange={(c) => props.onUpdatePage({ visible_if: c })}
          label="Only show this page conditionally"
          emptyHint="Add a question on an earlier page to show this page conditionally."
        />
      </div>

      <div className="space-y-2">
        {page.fields.length === 0 ? (
          <p className="rounded-token-sm border border-dashed border-line-strong/20 px-3 py-4 text-center text-xs text-fg-dim">
            No fields yet. Add one below.
          </p>
        ) : (
          page.fields.map((f, i) => (
            <FieldRow
              key={i}
              field={f}
              isFirst={i === 0}
              isLast={i === page.fields.length - 1}
              onEdit={() => props.onEditField(i)}
              onRemove={() => props.onRemoveField(i)}
              onMove={(dir) => props.onMoveField(i, dir)}
            />
          ))
        )}
      </div>

      <div className="mt-3">
        <Button variant="secondary" size="sm" iconLeft={<Plus className="h-4 w-4" />} onClick={() => setPaletteOpen(true)}>
          Add field
        </Button>
      </div>

      {paletteOpen ? (
        <FieldPalette
          onPick={(type) => {
            props.onAddField(type);
            setPaletteOpen(false);
          }}
          onClose={() => setPaletteOpen(false)}
        />
      ) : null}
    </div>
  );
}
